import { useMemo, useState, type ReactNode } from 'react'
import { useQuery } from '@tanstack/react-query'
import { CalendarIcon } from 'lucide-react'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'
import { Calendar } from '@/components/ui/calendar'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { useDepartments, useClasses, useSubclasses } from '@/hooks/useHierarchy'
import { useSeasons } from '@/hooks/useSeasons'
import { fetchHierarchyByLevel } from '@/api/productHierarchy'
import { queryKeys } from '@/lib/queryKeys'
import { cn } from '@/utils/cn'
import { ParetoChart } from './ParetoChart'
import {
  buildParetoBars,
  buildParetoTableRows,
  buildParetoTotalRow,
  formatCurrency0,
  formatDecimal2,
  formatInt,
  type ParetoFilterState,
} from './paretoCurveMockData'

const ALL = '__all__'

const PLAN_STATUSES = [
  'Draft',
  'In Progress',
  'Submitted',
  'Approved',
  'Rejected',
  'Sent Back',
]

const GLOSSARY: Array<{ term: string; description: string }> = [
  {
    term: 'Total Sales $',
    description:
      'Total revenue generated by the SKU or product group during the selected period.',
  },
  {
    term: 'Accumulative % (Sales)',
    description:
      'Running cumulative share of total sales, used to show how much of the business is explained by the top-selling SKUs.',
  },
  {
    term: 'Nbr Store',
    description:
      'Number of stores where the SKU or group is currently represented or selling.',
  },
  {
    term: 'Total Qty',
    description: 'Total units sold in the selected period.',
  },
  {
    term: 'Avg Qty / Week',
    description:
      'Average number of units sold per week, showing product sales velocity over time.',
  },
  {
    term: 'Total Profit $',
    description: 'Total gross profit after sales performance is accounted for.',
  },
  {
    term: 'Avg $ Profit / Week',
    description: 'Average number of units sold over time.',
  },
  {
    term: 'Avg Sales / Week / Store',
    description:
      'Average weekly sales per store, used to compare store-level productivity across products or groups.',
  },
  {
    term: 'Avg Sales / Week',
    description:
      "Average weekly sales count, regardless of store count, to show the item's total weekly selling pace.",
  },
]

function FilterField({
  label,
  children,
}: {
  label: string
  children: ReactNode
}) {
  return (
    <div className="flex flex-col gap-1">
      <label className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
        {label}
      </label>
      {children}
    </div>
  )
}

function formatTimestamp(date: Date): string {
  return date.toLocaleString('en-US', {
    month: 'numeric',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    second: '2-digit',
    hour12: true,
  })
}

function formatDate(date: Date | undefined): string {
  if (!date) return ''
  return date.toLocaleDateString('en-US', {
    month: '2-digit',
    day: '2-digit',
    year: 'numeric',
  })
}

export function ParetoCurveMockup() {
  const [divisionId, setDivisionId] = useState<string | null>(null)
  const [departmentId, setDepartmentId] = useState<string | null>(null)
  const [classId, setClassId] = useState<string | null>(null)
  const [subclassId, setSubclassId] = useState<string | null>(null)
  const [seasonId, setSeasonId] = useState<string | null>(null)
  const [planStatus, setPlanStatus] = useState<string | null>(null)
  const [dateFrom, setDateFrom] = useState<Date | undefined>(
    new Date(2026, 3, 5)
  )
  const [dateTo, setDateTo] = useState<Date | undefined>(new Date(2026, 10, 28))

  const lastUpdate = useMemo(() => formatTimestamp(new Date()), [])

  const { data: divisions, isLoading: divisionsLoading } = useQuery({
    queryKey: queryKeys.hierarchy.byLevelFiltered('DIVISION', null),
    queryFn: () => fetchHierarchyByLevel('DIVISION'),
    staleTime: 10 * 60 * 1000,
  })
  const { data: departments, isLoading: departmentsLoading } = useDepartments()
  const { data: classes, isLoading: classesLoading } = useClasses(departmentId)
  const { data: subclasses, isLoading: subclassesLoading } =
    useSubclasses(classId)
  const { data: seasons, isLoading: seasonsLoading } = useSeasons()

  const filters = useMemo<ParetoFilterState>(
    () => ({
      divisionId,
      departmentId,
      classId,
      subclassId,
      seasonId,
      planStatus,
      dateFrom: dateFrom ? dateFrom.toISOString().slice(0, 10) : null,
      dateTo: dateTo ? dateTo.toISOString().slice(0, 10) : null,
    }),
    [
      divisionId,
      departmentId,
      classId,
      subclassId,
      seasonId,
      planStatus,
      dateFrom,
      dateTo,
    ]
  )

  const bars = useMemo(() => buildParetoBars(filters), [filters])
  const tableRows = useMemo(() => buildParetoTableRows(filters), [filters])
  const totalRow = useMemo(() => buildParetoTotalRow(tableRows), [tableRows])

  return (
    <div className="flex flex-col gap-4 bg-[#fafafa] p-6">
      {/* Top strip: subtitle label + last update */}
      <div className="flex items-start justify-between">
        <span className="text-xs italic text-destructive">
          Product Profitability
        </span>
        <div className="text-right text-[11px] text-muted-foreground">
          <div>Last Update (TZ UTC-5)</div>
          <div>{lastUpdate}</div>
        </div>
      </div>

      {/* Filters row 1 */}
      <div className="grid grid-cols-4 gap-4">
        <FilterField label="Division">
          <Select
            value={divisionId ?? ALL}
            onValueChange={v => {
              setDivisionId(v === ALL ? null : v)
              setDepartmentId(null)
              setClassId(null)
              setSubclassId(null)
            }}
          >
            <SelectTrigger className="w-full bg-white">
              <SelectValue
                placeholder={divisionsLoading ? 'Loading...' : 'All'}
              />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={ALL}>All</SelectItem>
              {divisions?.map(d => (
                <SelectItem key={d.id} value={d.id}>
                  {d.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </FilterField>

        <FilterField label="Department">
          <Select
            value={departmentId ?? ALL}
            onValueChange={v => {
              setDepartmentId(v === ALL ? null : v)
              setClassId(null)
              setSubclassId(null)
            }}
          >
            <SelectTrigger className="w-full bg-white">
              <SelectValue
                placeholder={departmentsLoading ? 'Loading...' : 'All'}
              />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={ALL}>All</SelectItem>
              {departments?.map(d => (
                <SelectItem key={d.id} value={d.id}>
                  {d.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </FilterField>

        <FilterField label="Sales Date Range">
          <Popover>
            <PopoverTrigger asChild>
              <button
                type="button"
                className="flex h-9 w-full items-center justify-between rounded-md border border-input bg-white px-3 text-sm text-foreground shadow-xs cursor-pointer"
              >
                <span className="truncate">
                  {dateFrom && dateTo
                    ? `${formatDate(dateFrom)} – ${formatDate(dateTo)}`
                    : 'Select range'}
                </span>
                <CalendarIcon className="h-4 w-4 shrink-0 text-muted-foreground" />
              </button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0" align="start">
              <Calendar
                mode="range"
                selected={{ from: dateFrom, to: dateTo }}
                onSelect={range => {
                  setDateFrom(range?.from)
                  setDateTo(range?.to)
                }}
                numberOfMonths={2}
              />
            </PopoverContent>
          </Popover>
        </FilterField>

        <FilterField label="Season">
          <Select
            value={seasonId ?? ALL}
            onValueChange={v => setSeasonId(v === ALL ? null : v)}
          >
            <SelectTrigger className="w-full bg-white">
              <SelectValue
                placeholder={seasonsLoading ? 'Loading...' : 'All'}
              />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={ALL}>All</SelectItem>
              {seasons?.map(s => (
                <SelectItem key={s.id} value={s.id}>
                  {s.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </FilterField>
      </div>

      {/* Filters row 2 */}
      <div className="grid grid-cols-4 gap-4">
        <FilterField label="Class">
          <Select
            value={classId ?? ALL}
            disabled={!departmentId}
            onValueChange={v => {
              setClassId(v === ALL ? null : v)
              setSubclassId(null)
            }}
          >
            <SelectTrigger className="w-full bg-white">
              <SelectValue
                placeholder={classesLoading ? 'Loading...' : 'All'}
              />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={ALL}>All</SelectItem>
              {classes?.map(c => (
                <SelectItem key={c.id} value={c.id}>
                  {c.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </FilterField>

        <FilterField label="Subclass">
          <Select
            value={subclassId ?? ALL}
            disabled={!classId}
            onValueChange={v => setSubclassId(v === ALL ? null : v)}
          >
            <SelectTrigger className="w-full bg-white">
              <SelectValue
                placeholder={subclassesLoading ? 'Loading...' : 'All'}
              />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={ALL}>All</SelectItem>
              {subclasses?.map(s => (
                <SelectItem key={s.id} value={s.id}>
                  {s.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </FilterField>

        <FilterField label="Plan Status">
          <Select
            value={planStatus ?? ALL}
            onValueChange={v => setPlanStatus(v === ALL ? null : v)}
          >
            <SelectTrigger className="w-full bg-white">
              <SelectValue placeholder="All" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={ALL}>All</SelectItem>
              {PLAN_STATUSES.map(s => (
                <SelectItem key={s} value={s}>
                  {s}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </FilterField>

        <div className="flex flex-col justify-end">
          <span className="text-[11px] italic text-muted-foreground">
            *as a reference: the <strong>Date From</strong> and{' '}
            <strong>Date To</strong> indicate the season period.
          </span>
        </div>
      </div>

      {/* Chart + glossary */}
      <div className="grid grid-cols-[1fr_280px] gap-4">
        <ParetoChart bars={bars} />
        <div className="overflow-hidden rounded-lg border border-border bg-white p-4 shadow-sm">
          <ul className="space-y-3 text-[11px] leading-snug text-foreground">
            {GLOSSARY.map(item => (
              <li key={item.term}>
                <span className="font-semibold">{item.term}: </span>
                <span className="text-muted-foreground">
                  {item.description}
                </span>
              </li>
            ))}
          </ul>
        </div>
      </div>

      {/* Bottom table */}
      <div className="overflow-hidden rounded-lg border border-border bg-white shadow-sm">
        <Table className="text-xs [&_td]:whitespace-nowrap [&_td]:px-3 [&_td]:py-2 [&_th]:h-10 [&_th]:whitespace-nowrap [&_th]:px-3">
          <TableHeader>
            <TableRow className="hover:bg-transparent">
              <TableHead className="text-xs font-semibold text-foreground">
                Department
              </TableHead>
              <TableHead className="text-right text-xs font-semibold text-foreground">
                # of Stores
              </TableHead>
              <TableHead className="text-right text-xs font-semibold text-foreground">
                Sales $
              </TableHead>
              <TableHead className="text-right text-xs font-semibold text-foreground">
                Sales Units
              </TableHead>
              <TableHead className="text-right text-xs font-semibold text-foreground">
                Total Profit
              </TableHead>
              <TableHead className="text-right text-xs font-semibold text-foreground">
                Avg Qty / Week
              </TableHead>
              <TableHead className="text-right text-xs font-semibold text-foreground">
                Avg Sales $ / Week
              </TableHead>
              <TableHead className="text-right text-xs font-semibold text-foreground">
                Avg Sales / Week
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {tableRows.map(row => (
              <TableRow key={row.label} className="hover:bg-muted/20">
                <TableCell
                  className={cn(
                    'text-foreground',
                    row.level === 'department' && 'pl-6',
                    row.level === 'class' && 'pl-10',
                    row.level === 'subclass' && 'pl-14'
                  )}
                >
                  {row.label}
                </TableCell>
                <TableCell className="text-right tabular-nums">
                  {formatInt(row.numStores)}
                </TableCell>
                <TableCell className="text-right tabular-nums">
                  {formatCurrency0(row.salesDollar)}
                </TableCell>
                <TableCell className="text-right tabular-nums">
                  {formatInt(row.salesUnits)}
                </TableCell>
                <TableCell className="text-right tabular-nums">
                  {formatCurrency0(row.totalProfit)}
                </TableCell>
                <TableCell className="text-right tabular-nums">
                  {formatDecimal2(row.avgQtyPerWeek)}
                </TableCell>
                <TableCell className="text-right tabular-nums">
                  {formatCurrency0(row.avgSalesDollarPerWeek)}
                </TableCell>
                <TableCell className="text-right tabular-nums">
                  {formatCurrency0(row.avgSalesPerWeekStore)}
                </TableCell>
              </TableRow>
            ))}
            <TableRow className="border-t-2 border-border bg-muted/30 font-semibold hover:bg-muted/40">
              <TableCell className="font-semibold">{totalRow.label}</TableCell>
              <TableCell className="text-right tabular-nums font-semibold">
                {formatInt(totalRow.numStores)}
              </TableCell>
              <TableCell className="text-right tabular-nums font-semibold">
                {formatCurrency0(totalRow.salesDollar)}
              </TableCell>
              <TableCell className="text-right tabular-nums font-semibold">
                {formatInt(totalRow.salesUnits)}
              </TableCell>
              <TableCell className="text-right tabular-nums font-semibold">
                {formatCurrency0(totalRow.totalProfit)}
              </TableCell>
              <TableCell className="text-right tabular-nums font-semibold">
                {formatDecimal2(totalRow.avgQtyPerWeek)}
              </TableCell>
              <TableCell className="text-right tabular-nums font-semibold">
                {formatCurrency0(totalRow.avgSalesDollarPerWeek)}
              </TableCell>
              <TableCell className="text-right tabular-nums font-semibold">
                {formatCurrency0(totalRow.avgSalesPerWeekStore)}
              </TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </div>
    </div>
  )
}
